import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Radio,
  Button,
  Alert,
  Divider,
  Space,
  Tag,
  Descriptions,
  message,
  Spin,
  Input,
  Modal,
} from "antd";
import {
  CreditCardOutlined,
  DollarOutlined,
  CalendarOutlined,
  BuildOutlined,
  InfoCircleOutlined,
  EditOutlined,
  ShoppingCartOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  PlusOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import { useAuth0 } from "@auth0/auth0-react";
import { useOrganization } from "../../contexts/organization-context";
import { useSubscription } from "../../contexts/subscription-context";
import apiClient from "../../utils/axios";
import { StripeService } from "../../services/stripeService";
import { StripeSubscriptionStatus } from "../../utils/stripe";
import { CostBreakdown } from "../../components/CostBreakdown";
import { BillingService, BillingStatus } from "../../services/billingService";
import { PaymentMethodManager } from "../../components/PaymentMethodManager";
import { PricingService, PricingData } from "../../services/pricingService";
import { SubscriptionSetup } from "../../components/SubscriptionSetup";
import { Link } from "react-router";

const { Title, Text, Paragraph } = Typography;

interface SubscriptionData {
  organization_id: string;
  organization_name: string;
  current_facility_count: number;
  active_facility_count: number;
  max_facilities: number;
  billing_cycle: 'Monthly' | 'Annual';
  subscription_status: string;
  is_free_trial: boolean;
  free_trial_expires_at?: string;
  next_billing_date?: string;
  monthly_cost: number;
  annual_cost: number;
  monthly_cost_per_facility: number;
  annual_monthly_cost_per_facility: number;
  setup_fee_per_facility: number;
}

export const Subscription: React.FC = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const { ownedOrganizations, isOrganizationOwner, refreshOrganizations } = useOrganization();
  const { 
    subscriptionData, 
    stripeStatus, 
    billingStatus, 
    stripeCustomerInfo, 
    loading, 
    refreshSubscriptionData, 
    getPaymentStatusColor 
  } = useSubscription();

  const [cancelLoading, setCancelLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [billingModeLoading, setBillingModeLoading] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [editingEmail, setEditingEmail] = useState('');
  const [emailUpdateLoading, setEmailUpdateLoading] = useState(false);

  // Data is now managed by SubscriptionContext - no local fetching needed

  // Handle URL parameters for checkout success/cancel
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const cancelled = urlParams.get('cancelled');

    if (success) {
      // Sync billing cycle from Stripe immediately after successful checkout
      syncBillingCycleFromStripe();
      
      // Check if the user just subscribed during a trial
      if (subscriptionData?.is_free_trial) {
        message.success('Subscription created successfully! You\'re in your free trial period. Billing will start when your trial ends.');
      } else {
        message.success('Payment successful! Your subscription is now active.');
      }
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (cancelled) {
      message.info('Payment was cancelled. You can try again anytime.');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [subscriptionData]);

  const syncBillingCycleFromStripe = async () => {
    if (!ownedOrganizations.length) return;
    
    try {
      const token = await getAccessTokenSilently();
      // The subscription status endpoint already handles syncing billing cycle
      await StripeService.getSubscriptionStatus(ownedOrganizations[0].id, token);
      // Refresh subscription data to show updated billing cycle
      refreshSubscriptionData(true);
    } catch (error) {
      console.error("Error syncing billing cycle:", error);
      // Don't show error to user - this is a background sync
    }
  };



  const handleCancelSubscription = async () => {
    if (!subscriptionData || !ownedOrganizations.length) return;

    Modal.confirm({
      title: 'Cancel Subscription',
      icon: <ExclamationCircleOutlined />,
      content: 'Are you sure you want to cancel your subscription?',
      okText: 'Yes, Cancel Subscription',
      okType: 'danger',
      cancelText: 'Keep Subscription',
      onOk: async () => {
        try {
          setCancelLoading(true);
          const token = await getAccessTokenSilently();
          
          await StripeService.cancelSubscription(ownedOrganizations[0].id, token);
          
          message.success("Subscription cancelled successfully");
          await refreshSubscriptionData(true);
          
        } catch (error: any) {
          console.error("Error cancelling subscription:", error);
          message.error(error.response?.data?.detail || "Failed to cancel subscription");
        } finally {
          setCancelLoading(false);
        }
      }
    });
  };

  const handleCustomerPortal = async () => {
    if (!ownedOrganizations.length) return;

    try {
      setPortalLoading(true);
      const token = await getAccessTokenSilently();
      
      const portalSession = await BillingService.createCustomerPortalSession(
        ownedOrganizations[0].id,
        token
      );
      
      // Redirect to Stripe Customer Portal
      BillingService.redirectToCustomerPortal(portalSession.portal_url);
      
    } catch (error: any) {
      console.error("Error creating customer portal session:", error);
      message.error(error.response?.data?.detail || "Failed to open payment management portal");
    } finally {
      setPortalLoading(false);
    }
  };

  const handleBillingModeChange = async (mode: 'CreditCard' | 'Invoice') => {
    setBillingModeLoading(true);
    try {
      const token = await getAccessTokenSilently();
      await BillingService.updateBillingMode(ownedOrganizations[0].id, mode, token);
      message.success(`Billing mode updated to ${mode}`);
      // Refresh data to reflect changes
      await refreshSubscriptionData(true);
    } catch (error) {
      console.error("Error updating billing mode:", error);
      message.error("Failed to update billing mode");
    } finally {
      setBillingModeLoading(false);
    }
  };

  const handleUpdateCustomerEmail = async () => {
    if (!editingEmail.trim() || !editingEmail.includes('@')) {
      message.error('Please enter a valid email address');
      return;
    }

    setEmailUpdateLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const result = await StripeService.updateCustomerEmail(
        ownedOrganizations[0].id, 
        editingEmail.trim(), 
        token
      );
      
      if (result.success) {
        message.success('Billing email updated successfully');
        setIsEditingEmail(false);
        // Refresh subscription data to get updated customer info
        await refreshSubscriptionData(true);
      }
    } catch (error: any) {
      console.error("Error updating customer email:", error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to update billing email';
      message.error(errorMessage);
    } finally {
      setEmailUpdateLoading(false);
    }
  };

  const handleStartEditingEmail = () => {
    const currentEmail = stripeCustomerInfo?.customer_email || user?.email || '';
    setEditingEmail(currentEmail);
    setIsEditingEmail(true);
  };

  const handleCancelEditingEmail = () => {
    setIsEditingEmail(false);
    setEditingEmail('');
  };





  if (!isOrganizationOwner) {
    return (
      <Card>
        <Alert
          message="Access Denied"
          description="Only organization owners can manage subscriptions."
          type="error"
          showIcon
        />
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (!subscriptionData) {
    return (
      <Card>
        <Alert
          message="No Subscription Data"
          description="Unable to load subscription information."
          type="warning"
          showIcon
        />
      </Card>
    );
  }

  // Determine subscription state
  const hasActiveSubscription = stripeStatus?.has_subscription;
  const hasActiveSubscriptionOrTrial = stripeStatus?.has_subscription || subscriptionData.is_free_trial;
  
  // Detect if subscription was actually cancelled (had an active subscription that was cancelled)
  // vs never completed subscription (Stripe customer exists but no subscription history)
  const hasEverHadStripeCustomer = stripeCustomerInfo?.has_customer === true;
  
  // For now, we'll assume no subscriptions were actually cancelled since we don't track cancellation history
  // This prevents false "Subscription Cancelled" messages for users who started but never completed signup
  const isSubscriptionCancelled = false;
  
  // This is a new organization that has never subscribed or completed subscription
  const isNewOrganization = subscriptionData.subscription_status === 'Inactive' && 
                            !subscriptionData.is_free_trial;

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2} style={{ marginBottom: 24 }}>
        <CreditCardOutlined /> Subscription Management
      </Title>
      
      {/* New Organization Setup Guide */}
      {subscriptionData.is_free_trial && subscriptionData.active_facility_count === 0 && (
        <Alert
          message="Create your first facility to get started"
          description={
            <div>
              <Text>You need at least one facility to invite users and manage your team.</Text>
              <div style={{ marginTop: "12px" }}>
                <Link to="/organization/facilities">
                  <Button type="primary" icon={<PlusOutlined />}>
                    Add Your First Facility <ArrowRightOutlined />
                  </Button>
                </Link>
              </div>
            </div>
          }
          type="info"
          showIcon
          icon={<BuildOutlined />}
          style={{ marginBottom: 24 }}
        />
      )}
      
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={16}>
          <Card title="Current Subscription" style={{ marginBottom: 24 }}>
            <Descriptions column={1} bordered>
              <Descriptions.Item label="Organization">
                {subscriptionData.organization_name}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {subscriptionData.is_free_trial ? (
                  <Tag color="blue" icon={<CalendarOutlined />}>
                    Free Trial
                  </Tag>
                ) : stripeStatus?.has_subscription ? (
                  <Tag color="green" icon={<CheckCircleOutlined />}>
                    Active
                  </Tag>
                ) : (
                  <Tag color="orange" icon={<ExclamationCircleOutlined />}>
                    Inactive
                  </Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Total Facilities">
                <Space>
                  <BuildOutlined />
                  {subscriptionData.current_facility_count}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Active Facilities (Billable)">
                <Space>
                  <BuildOutlined />
                  <Text strong style={{ color: '#1890ff' }}>
                    {subscriptionData.active_facility_count}
                  </Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Billing Cycle">
                <Text strong>{subscriptionData.billing_cycle}</Text>
                {subscriptionData.is_free_trial && (
                  <Text type="secondary"> (billing starts when trial ends)</Text>
                )}
              </Descriptions.Item>
              {subscriptionData.free_trial_expires_at && (
                <Descriptions.Item label="Free Trial Expires">
                  <Text strong style={{ color: '#1890ff' }}>
                    {new Date(subscriptionData.free_trial_expires_at).toLocaleDateString()}
                  </Text>
                </Descriptions.Item>
              )}
              {subscriptionData.next_billing_date && (
                <Descriptions.Item label="Next Billing Date">
                  {new Date(subscriptionData.next_billing_date).toLocaleDateString()}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>



          {/* Show Subscribe section if no active subscription and has facilities */}
          {(!hasActiveSubscription && subscriptionData.active_facility_count > 0) && (
            <Card 
              title={
                <Space>
                  <ShoppingCartOutlined />
                  Subscribe
                </Space>
              } 
              style={{ marginBottom: 24 }}
            >
              <SubscriptionSetup
                organizationId={ownedOrganizations[0]?.id || ''}
                subscriptionData={subscriptionData}
                onSubscriptionSuccess={() => {
                  refreshSubscriptionData(true);
                }}
                showTitle={false}
                embedded={false}
              />
            </Card>
          )}

          {/* Billing Mode and Payment Method Management - Only show for active subscriptions */}
          {hasActiveSubscription && hasEverHadStripeCustomer && (
            <Card 
              title={
                <Space>
                  <CreditCardOutlined />
                  Billing & Payment Management
                </Space>
              } 
              style={{ marginBottom: 24 }}
            >
              {/* Billing Mode Selection */}
              <div style={{ marginBottom: 16 }}>
                <Text strong>Billing Mode:</Text>
                <div style={{ marginTop: 8 }}>
                  <Radio.Group 
                    value={billingStatus?.billing_mode || 'CreditCard'}
                    onChange={(e) => handleBillingModeChange(e.target.value)}
                    disabled={billingModeLoading}
                  >
                    <Radio.Button value="CreditCard">
                      <CreditCardOutlined /> Credit Card
                    </Radio.Button>
                    <Radio.Button value="Invoice">
                      <FileTextOutlined /> Invoice (NET-30)
                    </Radio.Button>
                  </Radio.Group>
                </div>
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">
                    {billingStatus?.billing_mode === 'Invoice' 
                      ? 'NET-30 invoice payment terms'
                      : 'Automatic credit card billing'
                    }
                  </Text>
                </div>
              </div>

              {/* Payment Status for Invoice Mode */}
              {billingStatus?.billing_mode === 'Invoice' && (
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Payment Status:</Text>
                  <div style={{ marginTop: 8 }}>
                    <Tag color={getPaymentStatusColor(billingStatus.payment_status || 'Current')}>
                      {(billingStatus.payment_status || 'Current').toUpperCase()}
                    </Tag>
                    {billingStatus.days_overdue && billingStatus.days_overdue > 0 && (
                      <Text type="danger" style={{ marginLeft: 8 }}>
                        {billingStatus.days_overdue} days overdue
                      </Text>
                    )}
                  </div>
                  {billingStatus.payment_due_date && (
                    <div style={{ marginTop: 4 }}>
                      <Text type="secondary">
                        Payment due: {BillingService.formatDate(billingStatus.payment_due_date)}
                      </Text>
                    </div>
                  )}

                  <br />

                  <Title level={5}>Invoice Email Address</Title>
                  <div style={{ fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
                    {!isEditingEmail ? (
                      <div style={{ 
                        backgroundColor: '#f5f5f5', 
                        padding: '8px 12px', 
                        borderRadius: '4px', 
                        marginBottom: '12px',
                        fontFamily: 'monospace',
                        fontSize: '13px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        <span>{stripeCustomerInfo?.customer_email || user?.email || 'Email address not available'}</span>
                        <Button 
                          size="small" 
                          type="link" 
                          icon={<EditOutlined />}
                          onClick={handleStartEditingEmail}
                          style={{ padding: '2px 4px' }}
                        >
                          Edit
                        </Button>
                      </div>
                    ) : (
                      <div style={{ marginBottom: '12px' }}>
                        <Input 
                          value={editingEmail}
                          onChange={(e) => setEditingEmail(e.target.value)}
                          placeholder="Enter billing email address"
                          size="small"
                          style={{ marginBottom: '8px' }}
                        />
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <Button 
                            size="small" 
                            type="primary"
                            loading={emailUpdateLoading}
                            onClick={handleUpdateCustomerEmail}
                          >
                            Save
                          </Button>
                          <Button 
                            size="small"
                            onClick={handleCancelEditingEmail}
                            disabled={emailUpdateLoading}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    <Text type="secondary">Invoices sent automatically with NET-30 payment terms. Pay online via secure portal included in invoice email.</Text>
                  </div>
                </div>
              )}

              {/* Embedded Payment Method Management for Credit Card Mode */}
              {billingStatus?.billing_mode === 'CreditCard' && (stripeStatus?.has_subscription || subscriptionData.is_free_trial) && (
                <div style={{ marginBottom: 16 }}>
                  <PaymentMethodManager 
                    organizationId={ownedOrganizations[0]?.id || ''}
                    onPaymentMethodsChange={() => {
                      // Optionally refresh billing status or show success message
                      refreshSubscriptionData(true);
                    }}
                  />
                  
                  {/* Advanced billing management fallback */}
                  <div style={{ marginTop: 16, textAlign: 'center' }}>
                    <Button 
                      type="link"
                      size="small"
                      loading={portalLoading}
                      onClick={handleCustomerPortal}
                    >
                      Advanced Billing Management
                    </Button>
                    <div style={{ marginTop: 4 }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Access full billing portal for invoices, billing history, and advanced settings
                      </Text>
                    </div>
                  </div>
                </div>
              )}

              {/* Subscription Management Actions */}
              {stripeStatus?.has_subscription && (
                <Divider />
              )}
              {stripeStatus?.has_subscription && (
                <div style={{ marginBottom: 16 }}>
                  <Text strong style={{ marginBottom: 12, display: 'block' }}>Subscription Actions:</Text>
                  <Space>
                    <Button 
                      danger
                      loading={cancelLoading}
                      onClick={handleCancelSubscription}
                      icon={<ExclamationCircleOutlined />}
                    >
                      Cancel Subscription
                    </Button>
                  </Space>
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Cancelling will stop future billing. Access continues until your current period ends.
                    </Text>
                  </div>
                </div>
              )}

              {/* Account Suspension Warning */}
              {billingStatus?.payment_status === 'Overdue' && (
                <Alert
                  message="Payment Overdue"
                  description={
                    billingStatus.days_until_suspension !== undefined && billingStatus.days_until_suspension > 0
                      ? `Service will be suspended in ${billingStatus.days_until_suspension} days. Please make payment to avoid interruption.`
                      : "Payment is overdue. Please make payment to avoid service interruption."
                  }
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}

              {billingStatus?.payment_status === 'Suspended' && (
                <Alert
                  message="Account Suspended"
                  description="Account suspended due to overdue payment. Contact support to restore access."
                  type="error"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}
            </Card>
          )}

          {/* Active Subscription Management - Only show for actual active subscriptions, not trials */}
          {hasActiveSubscription && !subscriptionData.is_free_trial && (
            <Card 
              title={
                <Space>
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  Active Subscription
                </Space>
              } 
              style={{ marginBottom: 24 }}
            >
              <Alert
                message="Subscription Active"
                description="Your subscription is active. You can cancel anytime."
                type="success"
                showIcon
                style={{ marginBottom: 16 }}
              />
              
              <Space>
                <Button 
                  danger
                  loading={cancelLoading}
                  onClick={handleCancelSubscription}
                  icon={<ExclamationCircleOutlined />}
                >
                  Cancel Subscription
                </Button>
              </Space>
            </Card>
          )}


        </Col>

        <Col xs={24} lg={8}>
          <div style={{ marginBottom: 24 }}>
            <CostBreakdown
              activeFacilities={subscriptionData.active_facility_count}
              billingCycle={subscriptionData.billing_cycle}
              isTrialActive={subscriptionData.is_free_trial}
              trialEndsAt={subscriptionData.free_trial_expires_at ? new Date(subscriptionData.free_trial_expires_at).toLocaleDateString() : undefined}
              pricingData={PricingService.extractPricingFromSubscription(subscriptionData)}
            />
          </div>
        </Col>
      </Row>
    </div>
  );
}; 